import { useState, useEffect } from 'react'
import BrandMark from './BrandMark'
import LevelSelect from './LevelSelect'
import { DEFAULT_SOLVE_LEVEL } from '../utils/solveLevels'

const KEY_SIGNATURES = ['C','G','D','A','E','B','F#','F','Bb','Eb','Ab','Db']
const KEY_SIGNATURES_MINOR = ['Cm','Gm','Dm','Am','Em','Bm','F#m','Fm','Bbm','Ebm','Abm','Dbm']
const TIME_SIGNATURES = ['4/4','3/4','2/4']
const BASE_RHYTHMS = [
  { value: 'w', label: '온음표' },
  { value: 'h', label: '2분음표' },
  { value: 'q', label: '4분음표' },
  { value: '8', label: '8분음표' },
]
const THEORY_BASES = [
  { value: 'baek', label: '백병동', title: '백병동 화성학 기준으로 분석/표기합니다.' },
  { value: 'kostka', label: 'Kostka&Payne', title: 'Kostka & Payne 『Tonal Harmony』 기준으로 분석/표기합니다.' },
]

export default function Toolbar({
  keySignature, onKeyChange,
  timeSignature, onTimeChange,
  tempoBpm, onTempoBpmChange,
  theoryBase, onTheoryBaseChange,
  volume, onVolumeChange,
  onPlayHarmony, onStopHarmony, canPlayHarmony, playingHarmony,
  onAnalyze,
  appMode = 'analyze', onSolve, solving = false,
  measureCount, onMeasureCountChange,
  baseRhythm, onBaseRhythmChange,
  solveLevel = DEFAULT_SOLVE_LEVEL, onSolveLevelChange,
}) {
  const [bpmDraft, setBpmDraft] = useState(String(tempoBpm ?? 120))
  const [measureDraft, setMeasureDraft] = useState(String(measureCount ?? 4))

  useEffect(() => {
    setBpmDraft(String(tempoBpm))
  }, [tempoBpm])

  useEffect(() => {
    setMeasureDraft(String(measureCount))
  }, [measureCount])

  return (
    <div className="toolbar">
      <div className="toolbar-group">
        <label className="tb-label">
          <span className="tb-label-text">Key</span>
          <select value={keySignature} onChange={e => onKeyChange(e.target.value)}>
            <optgroup label="장조">
              {KEY_SIGNATURES.map(k => <option key={k} value={k}>{k}</option>)}
            </optgroup>
            <optgroup label="단조">
              {KEY_SIGNATURES_MINOR.map(k => <option key={k} value={k}>{k}</option>)}
            </optgroup>
          </select>
        </label>

        <label className="tb-label">
          <span className="tb-label-text">박자</span>
          <select value={timeSignature} onChange={e => onTimeChange(e.target.value)}>
            {TIME_SIGNATURES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </label>

        {appMode === 'solve' && (
          <label className="tb-label">
            <span className="tb-label-text">중심 리듬</span>
            <select
              value={baseRhythm}
              onChange={e => onBaseRhythmChange(e.target.value)}
              title="멜로디·화성 교체 단위. 온음표=온음마다 화음 1개, 4분음표=4분마다 화음 1개."
            >
              {BASE_RHYTHMS.map(r => <option key={r.value} value={r.value}>{r.label}</option>)}
            </select>
          </label>
        )}

        {appMode === 'solve' && (
          <div className="tb-label">
            <span className="tb-label-text">레벨 설정</span>
            <LevelSelect
              value={solveLevel}
              onChange={(id) => onSolveLevelChange?.(id)}
            />
          </div>
        )}

        {appMode === 'solve' && (
          <label className="tb-label">
            <span className="tb-label-text">마디수</span>
            <input
              type="text"
              inputMode="numeric"
              autoComplete="off"
              spellCheck={false}
              className="tb-bpm-input"
              value={measureDraft}
              onChange={(e) => {
                const v = e.target.value.replace(/[^\d]/g, '')
                setMeasureDraft(v)
                if (v !== '') onMeasureCountChange(Math.max(1, Math.min(32, parseInt(v, 10))))
              }}
              onBlur={() => {
                if (measureDraft === '' || !/^\d+$/.test(measureDraft)) {
                  setMeasureDraft(String(measureCount))
                  return
                }
                onMeasureCountChange(Math.max(1, Math.min(32, parseInt(measureDraft, 10))))
              }}
              title="문제 생성 시 만들 마디 수 (1~32)."
            />
          </label>
        )}

        <label className="tb-label">
          <span className="tb-label-text">BPM</span>
          <input
            type="text"
            inputMode="numeric"
            autoComplete="off"
            spellCheck={false}
            className="tb-bpm-input"
            value={bpmDraft}
            onChange={(e) => {
              const v = e.target.value.replace(/[^\d]/g, '')
              setBpmDraft(v)
              if (v !== '') onTempoBpmChange(parseInt(v, 10))
            }}
            onBlur={() => {
              if (bpmDraft === '' || !/^\d+$/.test(bpmDraft)) {
                setBpmDraft(String(tempoBpm))
                return
              }
              onTempoBpmChange(parseInt(bpmDraft, 10))
            }}
            title="분당 박자 (20~320). 건반 미리듣기·화음 재생 길이에 반영됩니다."
          />
        </label>

        <label className="tb-label">
          <span className="tb-label-text">🔊 볼륨</span>
          <div className="tb-volume-row">
            <input
              type="range"
              min={0}
              max={100}
              step={1}
              value={volume}
              className="tb-volume-slider"
              onChange={(e) => onVolumeChange(parseInt(e.target.value, 10))}
              title="건반 미리듣기·화음 재생 볼륨"
            />
            <span className="tb-volume-value">{volume}</span>
          </div>
        </label>

        <label className="tb-label">
          <span className="tb-label-text">화성학이론</span>
          <div className="tb-segmented" role="group" aria-label="화성분석 기반지식 선택">
            {THEORY_BASES.map((tb) => (
              <button
                key={tb.value}
                type="button"
                className={`tb-segmented-btn ${theoryBase === tb.value ? 'active' : ''}`}
                onClick={() => onTheoryBaseChange(tb.value)}
                title={tb.title}
                aria-pressed={theoryBase === tb.value}
              >
                {tb.label}
              </button>
            ))}
          </div>
        </label>
      </div>

      <div className="toolbar-group">
        <button
          className="tb-btn tb-btn-orange"
          onClick={onPlayHarmony}
          disabled={!canPlayHarmony || playingHarmony}
          title="마디 안에서 박(음표 시작)마다 4성부를 동시에 재생합니다. 빨간 세로선이 현재 위치를 표시합니다."
        >
          ▶ 재생
        </button>
        <button
          type="button"
          className="tb-btn tb-btn-stop"
          onClick={onStopHarmony}
          disabled={!playingHarmony}
          title="화음 재생을 즉시 멈춥니다."
        >
          ■ 중단
        </button>
        <button
          type="button"
          className="tb-btn tb-btn-blue"
          onClick={onAnalyze}
          title={
            appMode === 'solve'
              ? '100점 만점으로 채점합니다(성부진행 위반·불완전 화음·빈 성부 감점). 결과는 아래 패널에 표시됩니다.'
              : 'kordAI: 브라우저에서 바로 판단합니다(로마숫자·전위·병행 5/8도·음역). 음이 없으면 안내 메시지만 표시됩니다.'
          }
        >
          ✦ <BrandMark className="brand-mark--inline" /> {appMode === 'solve' ? '채점' : '분석'}
        </button>
        {appMode === 'solve' && (
          <button
            type="button"
            className="tb-btn tb-btn-blue"
            onClick={onSolve}
            disabled={solving}
            title="주어진 성부를 바탕으로 나머지 3성을 채웁니다."
          >
            ✦ <BrandMark className="brand-mark--inline" /> {solving ? '푸는 중…' : '풀이'}
          </button>
        )}
      </div>
    </div>
  )
}
